import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { APP_VERSION } from '../lib/version';

// Franchisee-facing release notes, linked from the header version tag and the
// VersionBanner. Plain-language, benefit-first descriptions of what shipped.
//
// Adding a release: prepend an entry here and bump APP_VERSION to match its
// `version`. The entry matching APP_VERSION renders in full at the top; older
// ones are archived below it as collapsed rows, so the page stays short as
// releases accumulate.
type Release = {
  version: string;
  /** One-line teaser shown on the collapsed row in the archive. */
  summary: string;
  body: ReactNode;
};

const RELEASES: Release[] = [
  {
    version: '1.8.31',
    summary: 'Bigger review uploads, and a clear reason when one is refused',
    body: (
      <>
        <section className="whats-new-item">
          <h2>Review request uploads tell you what went wrong</h2>
          <p>
            If a contact spreadsheet was too large, the upload simply failed with no
            explanation. It now tells you the file is over the limit and what to do about it
            &mdash; and the limit itself has doubled to <strong>10 MB</strong>, which is
            plenty for a few thousand contacts.
          </p>
          <p>
            An upload interrupted by a dropped connection now says so too, rather than
            looking like the file was at fault.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.30',
    summary: 'Replies to your review requests now reach you',
    body: (
      <>
        <section className="whats-new-item">
          <h2>If someone replies to a review request, you&rsquo;ll get it</h2>
          <p>
            Review request emails are sent on your behalf from a no-reply address, so until
            now a client who pressed <strong>reply</strong> instead of clicking the link was
            writing into thin air. That matters most for the person replying to ask you to
            stop contacting them.
          </p>
          <p>
            Replies now go to your office &mdash; the same inbox your website enquiries
            already arrive in. Nothing to set up; we use the contact details on file for your
            territory. (The unsubscribe link in every email has always worked, and still
            does.)
          </p>
        </section>
        <section className="whats-new-item">
          <h2>Ask for Reviews has moved up the page</h2>
          <p>
            On the <strong>Google Business Profile</strong> tab it now sits just under your
            Views, Activity and Phone Calls numbers, above the two charts &mdash; closer to
            the top, and less scrolling to reach it.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.29',
    summary: 'A tidier Ask for Reviews panel',
    body: (
      <>
        <section className="whats-new-item">
          <h2>Ask for Reviews has moved, and is shorter</h2>
          <p>
            It now sits lower on the <strong>Google Business Profile</strong> tab, between
            your <strong>Health Score</strong> and your <strong>Action Items</strong> &mdash;
            right where the score tells you your review count and the action items tell you
            to grow it.
          </p>
          <p>
            It is also about a quarter shorter. The copy buttons sit beside their description
            rather than underneath, and the explanation of how uploads work is tighter.
          </p>
          <p>
            The option to separately record a send you made from your own email has gone
            &mdash; it was busywork. <strong>Sends so far</strong> now tracks the review
            emails the dashboard sends for you, along with the reviews you have gained since
            each one.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.28',
    summary: 'A clearer review-request section',
    body: (
      <>
        <section className="whats-new-item">
          <h2>Asking for reviews is easier to find</h2>
          <p>
            In the <strong>Ask for Reviews</strong> panel, uploading a spreadsheet is now
            clearly the main action, with the instructions and a sample file alongside it.
            Recording a send you made from your own email &mdash; the less common case &mdash;
            moved to a small link underneath, so the two are no longer easy to confuse.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.27',
    summary: 'A sample spreadsheet to copy, and review emails that greet people by name',
    body: (
      <>
        <section className="whats-new-item">
          <h2>Your review emails now say the person&rsquo;s name</h2>
          <p>
            When you upload a spreadsheet of contacts, each email is addressed to the person
            individually &mdash; Jane gets &ldquo;Hello Jane&rdquo; rather than
            &ldquo;Hello&rdquo;. A named greeting is read far more often than an anonymous one,
            and a review request only works if it gets opened.
          </p>
          <p>
            We take the first name from your spreadsheet. If your file has no name column the
            emails still go out, just without the name, and the dashboard tells you which
            happened after each send.
          </p>
        </section>
        <section className="whats-new-item">
          <h2>A sample spreadsheet you can copy</h2>
          <p>
            The upload section now has a <strong>sample spreadsheet</strong> you can download:
            three columns &mdash; first name, last name, email &mdash; so there is no guessing
            about the format. Fill it in, upload it, done.
          </p>
          <p>
            A note on file types: <strong>.xlsx</strong> and <strong>.csv</strong> both work.
            If you have an older <strong>.xls</strong> file, open it in Excel and use
            &ldquo;Save As&rdquo; to make a .xlsx first &mdash; the dashboard will tell you if
            you forget.
          </p>
        </section>
        <section className="whats-new-item">
          <h2>Clearer buttons</h2>
          <p>
            The upload is now the main action, headed <em>Upload a spreadsheet</em>. The
            smaller button below it, for recording a send you made from your own email, says
            what it does: <em>Log a send you made yourself</em>.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.26',
    summary: 'Ask for Google reviews — your link, a QR code, and we can send the emails for you',
    body: (
      <>
        <section className="whats-new-item">
          <h2>A new Ask for Reviews panel</h2>
          <p>
            Google reviews are the single biggest lever on whether families find you in the
            Maps results, and most offices simply never get round to asking. There is now a
            panel at the top of <strong>Metrics &rarr; Google Business Profile</strong> built
            to make asking a two-minute job.
          </p>
          <p>
            It gives you your <strong>review link</strong> &mdash; the one that opens the Google
            review box straight away, with no searching and no hunting for the right listing
            &mdash; plus a <strong>QR code</strong> you can download for a card, a folder or a
            window, and ready-written <strong>email and text</strong> you can copy and send from
            your own account.
          </p>
        </section>
        <section className="whats-new-item">
          <h2>Or upload your contacts and we&rsquo;ll send them</h2>
          <p>
            Upload a spreadsheet of your own clients or contacts and the dashboard emails each
            one your review link. You confirm the list is yours, and we take it from there.
          </p>
          <p>
            <strong>We do not keep your list.</strong> The file is read to send the emails and
            then discarded &mdash; no names and no email addresses are stored anywhere in the
            dashboard, and the list cannot be viewed, exported or re-sent afterwards. Every
            email carries an unsubscribe link, and anyone who opts out is skipped automatically
            on every later send.
          </p>
          <p>
            Each send is recorded with a date, how you sent it and how many people you reached,
            alongside the number of reviews you have gained since &mdash; so you can see which
            asks actually worked.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.25',
    summary: 'Call metrics now count every territory you own',
    body: (
      <>
        <section className="whats-new-item">
          <h2>Your call metrics now include all of your territories</h2>
          <p>
            If you own more than one territory, the Call Tracking tab was telling you two
            different stories at once. The list of calls underneath showed calls to{' '}
            <strong>all</strong> of your territories, but the metric tiles above it &mdash; Total
            Calls, Answer Rate, Quality Leads, Assessments Booked &mdash; counted only your main
            one.
          </p>
          <p>
            Where a main territory had no tracking number of its own, that gap was stark: a full
            page of answered calls sitting under an <strong>Answer Rate of 0%</strong>. Nothing
            was wrong with the calls, and nothing was missing from the list. The tiles were simply
            counting a smaller set than the table they sat on top of.
          </p>
          <p>
            The tiles and the Conversion Funnel chart now count exactly the calls shown in the
            list. If you own a single territory, nothing about your numbers changes.
          </p>
        </section>
        <section className="whats-new-item">
          <h2>No calls yet reads as no calls, not 0%</h2>
          <p>
            When there are no calls at all in the date range you have selected, Answer Rate now
            shows a dash rather than <em>0%</em>. An empty month is not the same as a month where
            nobody picked up, and it should not look like one.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.24',
    summary: 'Who manages which listing, photos that no longer bounce, and a false mismatch fixed',
    body: (
      <>
        <section className="whats-new-item">
          <h2>Directory Listings now says who looks after each one</h2>
          <p>
            Almost everything on the Directory Listings tab is published for you through Yext.
            Two are not: <strong>Facebook</strong> and <strong>Google Business Profile</strong> are
            managed directly by the Amada marketing team.
          </p>
          <p>
            Yext still reports a status for those two, which was misleading &mdash; a
            &ldquo;Suppressed&rdquo; on your Google listing read as something Yext could fix, when
            nobody at Yext controls it. Both cards are now marked{' '}
            <em>Managed by Amada marketing</em>, and the tab says where to take a problem with
            them: straight to marketing, not into a Yext ticket.
          </p>
          <p>
            Your Live, Total and Live Rate figures are unchanged &mdash; they still describe
            everything Yext reports on.
          </p>
        </section>

        <section className="whats-new-item">
          <h2>Content photos: WebP files are converted, not rejected</h2>
          <p>
            Photos saved from a browser are often WebP files, and the content form used to turn
            them away &mdash; which usually meant finding the picture again and re-saving it in
            another format.
          </p>
          <p>
            The form now converts a WebP to JPG for you when you attach it. A submission still
            never goes out without its photos; it just no longer bounces over the file type.
          </p>
        </section>

        <section className="whats-new-item">
          <h2>A false mismatch on the Live Website tab</h2>
          <p>
            The address check compares what Zoho holds for your office against what Yext is
            syndicating, and flags a mismatch when they disagree. Some locations were being flagged
            even though the two addresses were identical.
          </p>
          <p>
            The cause was a part of the address &mdash; usually the ZIP &mdash; that was missing
            from <em>both</em> records. The check counted that as a disagreement, when in fact
            there was nothing to disagree about. Those locations now show as matching.
          </p>
          <p>
            Genuine differences are still flagged exactly as before, including the case where one
            record has a ZIP and the other doesn't.
          </p>
        </section>

        <section className="whats-new-item">
          <h2>Also in this release</h2>
          <p>
            The dashboard tab now carries the Amada mark instead of a blank browser icon, so it is
            easier to pick out among open tabs.
          </p>
          <p>
            Behind the scenes, a good deal of work went into the content tools the marketing team
            uses to refresh your website pages &mdash; building the page list from the site itself,
            reviewing and editing a draft before it goes anywhere, and publishing an approved one.
            Nothing there changes what you do, but it is how page refreshes reach you faster.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.22',
    summary: 'TikTok and ChatGPT pilots, and a one-click way to reach marketing',
    body: (
      <>
        <section className="whats-new-item">
          <h2>Two new channels under Paid Performance</h2>
          <p>
            <strong>Paid Performance</strong> now has <strong>TikTok</strong> and{' '}
            <strong>ChatGPT</strong> tabs alongside Google Ads, Meta and YouTube. Neither is
            reporting numbers yet &mdash; we're running pilots first.
          </p>
          <p>
            If you'd like your location included in either pilot, open the tab and use the email
            link. It opens a message to the marketing team, already written, so all you have to do
            is send it.
          </p>
        </section>
        <section className="whats-new-item">
          <h2>A Send Us An Email button in the header</h2>
          <p>
            Next to the <strong>Menu</strong> button, top-left, there's now a{' '}
            <strong>Send Us An Email</strong> button. It opens a message to the marketing team from
            wherever you are in the dashboard, so you don't have to go looking for the address.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.19',
    summary: 'Chatbot Leads now tells you where your chat leads actually are',
    body: (
      <>
        <section className="whats-new-item">
          <h2>A Chatbot Leads tab, with a straight answer</h2>
          <p>
            Under <strong>Calls and Leads</strong> you'll now see a{' '}
            <strong>Chatbot Leads</strong> tab. If the chatbot on your website is still run by
            Digital AirStrike &mdash; which it is for most locations today &mdash; the tab says so
            and gives you a button straight through to your Digital AirStrike login, which is where
            those leads are collected.
          </p>
          <p>
            Previously the tab was simply hidden unless your chatbot had already been moved to the
            dashboard, which left no indication of where to look.
          </p>
          <p>
            When your chatbot does move over, your leads start appearing on that tab automatically
            and you won't need the separate login any more.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.18',
    summary: 'Scheduled posts now show as Scheduled instead of reporting as published',
    body: (
      <>
        <section className="whats-new-item">
          <h2>Your scheduled posts were being reported wrongly</h2>
          <p>
            If you picked a future publish date for a blog post, your{' '}
            <strong>Content</strong> tab marked it <strong>Published</strong> as soon as marketing
            approved it &mdash; with that day's date &mdash; even though the post was correctly
            queued on your site for the date you chose.
          </p>
          <p>
            Schedule four posts a week apart and all four would flip to &ldquo;Published&rdquo; the
            same afternoon. Understandably, that looked like the scheduling had failed and
            everything had gone out at once.
          </p>
          <p>
            <strong>The posts themselves were never affected.</strong> They went out on the dates
            you picked. What was wrong was what the dashboard told you.
          </p>
        </section>
        <section className="whats-new-item">
          <h2>What you'll see now</h2>
          <p>
            A post with a future date stays marked <strong>Scheduled</strong>, showing the date it
            will go live, right up until it does &mdash; then it turns to{' '}
            <strong>Published</strong> with the real date. The &ldquo;view your post&rdquo; link no
            longer appears until the post is actually live, so you won't land on an error page.
          </p>
          <p>
            Posts already scheduled before this release have been corrected too.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.17',
    summary: 'Your Meta ad images are showing again on the Paid Performance tab',
    body: (
      <>
        <section className="whats-new-item">
          <h2>The broken image boxes on your Meta ads are fixed</h2>
          <p>
            If you run Meta ads, the <strong>Paid Performance &rarr; Meta</strong> tab lists your
            active ad creatives. For some of those ads the picture was showing as a broken image
            box instead of the ad itself. The wording, headline and stats beside it were always
            correct &mdash; only the picture failed to load.
          </p>
          <p>
            The cause was on our side: we were altering the image address Meta gives us in order to
            request a larger version, and Meta rejected the altered address. We now ask Meta for the
            right size up front, so the image loads as it should.
          </p>
          <p>
            Nothing else about the tab has changed, and none of your numbers are affected.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.16',
    summary: 'Two tabs renamed — "Organic" is now "Metrics", "Paid" is now "Paid Performance"',
    body: (
      <>
        <section className="whats-new-item">
          <h2>The Organic tab is now called Metrics</h2>
          <p>
            The name was misleading. That tab has never been only about organic search &mdash; it
            also holds your website traffic from every source, your{' '}
            <strong>Google Business Profile</strong>, and your{' '}
            <strong>Directory Listings</strong>. Calling it &ldquo;Organic&rdquo; suggested you
            were looking at a narrower slice than you actually were.
          </p>
          <p>
            It is now <strong>Metrics</strong>. Nothing inside it has moved, and no numbers have
            changed &mdash; Website, Rankings, Google Business Profile and Directory Listings are
            exactly where they were.
          </p>
        </section>
        <section className="whats-new-item">
          <h2>The Paid tab is now Paid Performance</h2>
          <p>
            A clearer name for the same tab: your Google Ads and Meta results. Nothing inside it
            has changed either.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.13',
    summary: 'Calls to your SEO number now say so, instead of "Direct"',
    body: (
      <>
        <section className="whats-new-item">
          <h2>Your SEO calls are now labeled correctly</h2>
          <p>
            In the last release we changed calls that read <em>disabled</em> to read
            <strong> Direct</strong>. That was wrong, and this release fixes it.
          </p>
          <p>
            Those calls came in on your <strong>SEO tracking number</strong> &mdash; the one on your
            Google Business Profile and your directory listings. They now show that number&rsquo;s
            name, the same as every other call to it (for example{' '}
            <strong>AF-208 SEO - Tulsa, OK</strong>), so your call list no longer splits the same
            number into two different labels.
          </p>
          <p>
            It matters because &ldquo;Direct&rdquo; suggests somebody who already had your number.
            Nobody has a tracking number memorized &mdash; these are people who found you through
            search, and calling them Direct quietly undercounted your local SEO.
          </p>
          <p>
            As before, your call totals, answer rates and every other number are unchanged. Only the
            wording in the Source column has moved.
          </p>
        </section>
        <section className="whats-new-item">
          <h2>You can see which number each call came in on</h2>
          <p>
            Open any call and the <strong>Source</strong> line now shows the number that was dialed
            underneath it &mdash; <em>via (918) 359-8776</em> &mdash; so you can tell your SEO number
            from your Google Ads number at a glance. Your CSV download already carries the same thing
            in its <strong>Tracking Number</strong> column.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.12',
    summary: 'Call sources now read in plain English instead of "disabled"',
    body: (
      <>
        <section className="whats-new-item">
          <h2>&ldquo;disabled&rdquo; in your call list now says &ldquo;Direct&rdquo;</h2>
          <p>
            In <strong>Calls and Leads &rarr; Call Tracking</strong>, some calls showed the
            word <strong>disabled</strong> in the Source column. That was a raw label from our
            call-tracking provider leaking onto the screen &mdash; it did <em>not</em> mean anything
            was switched off, and nothing was wrong with your phone number.
          </p>
          <p>
            Those calls are people who dialed your tracked number <strong>directly</strong> &mdash;
            from your Google Business Profile, an online directory, or a number they already had
            saved. They now show as <strong>Direct</strong>. A few other calls showed labels like{' '}
            <em>google_paid</em> or <em>landing_url</em>; those now read{' '}
            <strong>Google Paid</strong> and <strong>Landing Page</strong>.
          </p>
          <p>
            Your call totals, answer rates, quality leads and every other number are exactly the
            same &mdash; only the wording in the Source column has changed.
          </p>
        </section>
        <section className="whats-new-item">
          <h2>You can now see which tracking number a call came in on</h2>
          <p>
            Open any call and the <strong>Source</strong> line now shows the tracking number it
            arrived on underneath it, so you can tell your SEO number from your Google Ads number at
            a glance. Your CSV download has a new <strong>Tracker</strong> column with the same
            information.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.5',
    summary: 'Groundwork for website chat — nothing changes on your dashboard yet',
    body: (
      <>
        <section className="whats-new-item">
          <h2>Nothing to do this time</h2>
          <p>
            This release is groundwork for a website chat feature we&rsquo;re building, and none of
            it is switched on for your location yet. Your dashboard works exactly as it did
            before &mdash; no tabs have moved and no numbers have changed.
          </p>
          <p>
            We&rsquo;ll tell you properly when there is something for you to use.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.3',
    summary: 'An unfinished section that briefly appeared has been put away again',
    body: (
      <>
        <section className="whats-new-item">
          <h2>The Chat tab has gone back into development</h2>
          <p>
            A <strong>Chat</strong> tab appeared on your dashboard for a short time. It belongs to
            a website chat assistant we are still building, it was not meant to be visible yet, and
            it is now hidden again.
          </p>
          <p>
            Nothing you did in it had any effect, and nothing else on your dashboard is affected.
            You will hear about the chat assistant properly, with training, when it is ready.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.2',
    summary: 'The Net Promoter Score map is readable now',
    body: (
      <>
        <section className="whats-new-item">
          <h2>A map you can actually read</h2>
          <p>
            The United States map on the <strong>Net Promoter Score</strong> page was drawn in
            near-white on a white panel, which made the country and the state lines almost
            impossible to pick out &mdash; and worse once it was projected on a screen or dropped
            into a slide.
          </p>
          <p>
            The land, the state borders and the grey dots for locations without enough responses
            are all darker now, so the shape of the country reads clearly while the coloured score
            dots still stand out. Nothing about the scores or the colour scale has changed.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8.1',
    summary: 'The Organic tab no longer breaks when a territory has no website analytics',
    body: (
      <>
        <section className="whats-new-item">
          <h2>The Organic tab opens for every territory</h2>
          <p>
            If your territory doesn&rsquo;t have a website of its own &mdash; because it shares one
            with a neighbouring territory, for example &mdash; opening the <strong>Organic</strong>{' '}
            tab would leave the page stuck instead of loading. It now opens normally and simply
            tells you that no Google Analytics data is available, while the{' '}
            <strong>Rankings</strong>, <strong>Google Business Profile</strong> and{' '}
            <strong>Directory Listings</strong> sub-tabs work as they always have.
          </p>
          <p>
            Nothing changes for territories that already had website analytics &mdash; your
            sessions, chats, phone clicks and charts are exactly as before.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.8',
    summary: 'A photo rights check when you submit content, and a stricter address comparison',
    body: (
      <>
        <section className="whats-new-item">
          <h2>Confirming you can use the photos you upload</h2>
          <p>
            When you attach photos to a blog or News &amp; Events submission, you&rsquo;ll now see a
            second checkbox under the photo/video release: a confirmation that the images are your
            own or licensed for commercial use. It only appears when you&rsquo;ve attached at least
            one photo, and text-only posts are unaffected.
          </p>
          <p>
            It matters more than it might look. A photo pulled from Google Images or another
            company&rsquo;s website is almost always copyrighted, and a copyright claim is made
            against <strong>you personally</strong> &mdash; not against Amada Franchise, Inc. These
            claims are common and they are expensive.
          </p>
        </section>

        <section className="whats-new-item">
          <h2>Where to get photos that carry no risk</h2>
          <p>
            You don&rsquo;t have to hunt for images. <strong>Canva</strong> and{' '}
            <strong>AFI&rsquo;s photo library</strong> are both licensed for commercial use, so
            anything you take from them is safe to publish on your site. Your own photos &mdash;
            your team, your events, your office &mdash; are always the strongest option, and the one
            readers respond to most.
          </p>
        </section>

        <section className="whats-new-item">
          <h2>The Listings tab is stricter about your address</h2>
          <p>
            The comparison between your Zoho address and your Yext listing used to call close
            matches &ldquo;matching.&rdquo; It now compares them strictly and shows exactly which
            words differ, in bold, so a wrong suite number or an abbreviated street name is
            something you can see at a glance instead of having to spot yourself.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.7',
    summary: 'A full user manual for the dashboard, in the Menu',
    body: (
      <>
        <section className="whats-new-item">
          <h2>There&rsquo;s now a manual for all of this</h2>
          <p>
            The dashboard has grown a lot, and until now the only way to learn a tab was to poke at
            it. Open the <strong>Menu</strong> in the top-left corner and choose{' '}
            <strong>User Manual</strong> for a written walk-through of the whole thing.
          </p>
          <p>
            It covers every tab in the order they appear on your screen &mdash; what each one shows,
            what the numbers mean, and what to actually do with them. There are sections on reading
            your call funnel, getting into Google&rsquo;s Maps 3-pack, submitting content, the Ad
            Match programs, and Net Promoter Score.
          </p>
        </section>

        <section className="whats-new-item">
          <h2>Where every number comes from</h2>
          <p>
            The question we get asked most often is some version of &ldquo;where does this figure
            come from, and how old is it?&rdquo; The manual answers it directly: each section names
            its source and its refresh schedule, and there&rsquo;s a single reference table near the
            bottom listing all of them together &mdash; which panels update nightly, which are live,
            which refresh weekly.
          </p>
          <p>
            If a number ever looks wrong, that table is the fastest way to tell the difference
            between a real problem and a panel that simply hasn&rsquo;t refreshed yet.
          </p>
        </section>

        <section className="whats-new-item">
          <h2>Start with the three cards at the top</h2>
          <p>
            One thing worth acting on today: the manual explains why the three cards above the tabs
            &mdash; <strong>Web Presence</strong>, <strong>Critical Info</strong> and{' '}
            <strong>PPC Campaign</strong> &mdash; matter more than anything else on the page. They
            feed every other tab. If your site URL, your webform distribution emails or your office
            address are wrong there, it quietly costs you leads everywhere else.
          </p>
          <p>Take two minutes, check all three, and submit a ticket for anything that looks off.</p>
        </section>
      </>
    ),
  },
  {
    version: '1.6',
    summary: 'Marketing announcements now land in the dashboard, starting with the October box',
    body: (
      <>
        <section className="whats-new-item">
          <h2>October&rsquo;s Marketing in a Box is available to order</h2>
          <p>
            The October kit is live at <strong>TheAmadaStore.com</strong> and orders are filled on a
            first-come, first-served basis. Everything that&rsquo;s in the box — the puzzle books,
            the fuzzy slipper socks, the bone pens, the cork coasters, the tote bags, the Halloween
            and World Stroke Day stickers and the flatpack goodie boxes — is listed on the{' '}
            <Link to="/announcements/october-2026-miab">October announcement page</Link>, along with
            the shipping schedule.
          </p>
        </section>

        <section className="whats-new-item">
          <h2>Announcements you can come back to</h2>
          <p>
            Marketing news used to reach you only by email, which meant it was easy to lose. Time-
            sensitive announcements now also appear as a banner across the top of your dashboard,
            linking to a full write-up you can return to at any time — the banner clears itself once
            the news is no longer current, but the page it points to stays put.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.5',
    summary: 'Video and social posts in your blog, and survey links that look right when you share them',
    body: (
      <>
        {/* 1. Rich-media embeds */}
        <section className="whats-new-item">
          <h2>Put video and social posts straight into your blog</h2>
          <p>
            When you write a blog post, you can now drop in a{' '}
            <strong>video or social media post</strong> and it will appear as a real, playable
            embed on your site — not just a link your readers have to click away to.
          </p>
          <p>
            Paste a link from <strong>YouTube</strong>, <strong>X</strong>,{' '}
            <strong>Instagram</strong>, <strong>Vimeo</strong>, or <strong>TikTok</strong> and the
            editor recognises it and shows you a preview right there. When the post publishes, the
            real player goes onto your page.
          </p>
          <p>
            This is a good way to reuse what you already have. A caregiver-appreciation reel, a
            community event you posted about, a testimonial video — it can now live on your website
            as well, without anyone touching WordPress.
          </p>
        </section>

        {/* 2. NPS link previews + mobile scale */}
        <section className="whats-new-item">
          <h2>Your feedback links now explain themselves</h2>
          <p>
            When you shared a <strong>Net Promoter Score</strong> link by text or email, the preview
            that appeared underneath it said &ldquo;Amada Marketing Dashboard&rdquo; — which meant
            nothing to a client or a caregiver, and understandably made some people wary of clicking.
          </p>
          <p>
            Those previews now name <strong>your office</strong> and say what the link is for — for
            example <em>&ldquo;Share Your Feedback &mdash; Amada Senior Care of Nashville&rdquo;</em>{' '}
            with a line explaining it&rsquo;s one question and takes about thirty seconds. Nothing
            changes about how you share them; they simply look like what they are now.
          </p>
          <p>
            We also fixed the survey itself on phones. The <strong>0&ndash;10 scale</strong> used to
            run off the right-hand edge on a narrow screen, so the highest scores could be cut off
            and unreachable — on the exact devices most people open a text on. The scale now fits
            properly, with buttons big enough to tap comfortably.
          </p>
          <p>
            One thing to know: if you already sent a link to someone, their phone may have saved the
            old preview and will keep showing it. Anything you send from now on gets the new one.
          </p>
        </section>

        {/* 3. Headings */}
        <section className="whats-new-item">
          <h2>Tidier headings in the content you submit</h2>
          <p>
            When you paste text in from Word, Google Docs, or another website, it often brings
            invisible formatting with it — most commonly a second page title, or empty headings that
            leave odd gaps on the published page.
          </p>
          <p>
            Submissions are now cleaned up automatically: your headline stays the single main title
            of the page, any duplicate title in the body is demoted to a subheading, and blank
            headings are removed. Your published pages look the way you intended, and search engines
            get a clear signal about what each page is actually about.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.4',
    summary: 'Net Promoter Score — hear from your caregivers, clients, and referral partners',
    body: (
      <>
        {/* 1. What it is + where to find it */}
        <section className="whats-new-item">
          <h2>Find out what your caregivers, clients, and referral partners really think</h2>
          <p>
            The dashboard now has <strong>Net Promoter Score (NPS)</strong> — a simple, proven way
            to measure how well your location is doing in the eyes of the three groups your business
            depends on. You&rsquo;ll find it under the new <strong>Menu</strong> button in the
            top-left corner, at <strong>Net Promoter Score</strong>.
          </p>
          <p>
            Instead of guessing whether your caregivers are happy or whether your referral sources
            would actually vouch for you, you can now ask them directly — and watch the number move
            over time.
          </p>
        </section>

        {/* 2. Three links, three questions */}
        <section className="whats-new-item">
          <h2>Three links, each asking the right question</h2>
          <p>
            Your location gets <strong>three shareable links</strong>, one for each audience. Each
            one asks a single question written specifically for that relationship:
          </p>
          <ul>
            <li>
              <strong>Caregivers</strong> — &ldquo;How likely are you to recommend working here to a
              friend or fellow caregiver?&rdquo;
            </li>
            <li>
              <strong>Clients &amp; families</strong> — &ldquo;How likely are you to recommend our
              care services to a friend or family member?&rdquo;
            </li>
            <li>
              <strong>Referral sources</strong> — &ldquo;How likely are you to recommend partnering
              with us to a colleague?&rdquo;
            </li>
          </ul>
          <p>
            Copy a link and send it however you already talk to that group — a text, an email
            signature, a QR code on a flyer, or a follow-up after a visit. There&rsquo;s{' '}
            <strong>no login required</strong> to answer, so nobody has to create an account or
            remember a password. They rate you <strong>0 to 10</strong> and can add a comment if
            they want to. That&rsquo;s the whole survey — it takes about ten seconds.
          </p>
        </section>

        {/* 3. The score */}
        <section className="whats-new-item">
          <h2>One score, and the story behind it</h2>
          <p>
            Your NPS is calculated the standard way: the percentage of{' '}
            <strong>promoters</strong> (people who scored you 9&ndash;10) minus the percentage of{' '}
            <strong>detractors</strong> (0&ndash;6). Scores of 7&ndash;8 are{' '}
            <strong>passives</strong> — they count toward your total responses but not for or
            against you. The result lands somewhere between &minus;100 and +100.
          </p>
          <p>
            You see your <strong>overall score</strong> plus a{' '}
            <strong>separate score for each audience</strong>, which is where it gets genuinely
            useful. A location can have delighted clients and frustrated caregivers at the same
            time, and a single blended number would hide that. Splitting it out shows you exactly
            which relationship needs attention.
          </p>
        </section>

        {/* 4. Comments */}
        <section className="whats-new-item">
          <h2>Read the feedback in their own words</h2>
          <p>
            Scores tell you <em>that</em> something is working or not; comments tell you{' '}
            <em>why</em>. Every written comment appears in your results, newest first, tagged with
            the score it came with and whether that person was a promoter, passive, or detractor —
            so you can immediately see what your happiest and unhappiest respondents are saying.
          </p>
        </section>

        {/* 5. Privacy */}
        <section className="whats-new-item">
          <h2>Completely anonymous — on purpose</h2>
          <p>
            Responses are anonymous by design. We collect{' '}
            <strong>no names, no email addresses, and no phone numbers</strong> — there&rsquo;s
            nowhere to enter them. The link itself is a scrambled code that doesn&rsquo;t reveal
            your location or which audience it was sent to, so it can be shared freely without
            exposing anything.
          </p>
          <p>
            This is deliberate: people answer honestly when they know they can&rsquo;t be
            identified, and honest feedback is the entire point. The comment box is the only free
            field, and it reminds respondents not to include personal details.
          </p>
        </section>

        {/* 6. Rotation */}
        <section className="whats-new-item">
          <h2>Replace a link any time, without losing your history</h2>
          <p>
            If a link ends up somewhere you didn&rsquo;t intend — forwarded outside your territory,
            or posted publicly — you can <strong>rotate</strong> it. That instantly retires the old
            link so it stops accepting responses, and gives you a fresh one to share.
          </p>
          <p>
            Everything already collected stays exactly where it is. Rotating changes the door, not
            the room: your scores, your history, and your comments are all preserved.
          </p>
        </section>
      </>
    ),
  },
  {
    version: '1.2',
    summary: 'Clearer charts and plain-English definitions on the Website tab',
    body: (
      <section className="whats-new-item">
        <h2>Charts on the Website tab that explain themselves</h2>
        <p>
          The <strong>New Users</strong> chart now labels both of its axes, so it&rsquo;s clear at a
          glance that you&rsquo;re looking at new users by week of the year, instead of having to
          work it out from the numbers.
        </p>
        <p>
          We&rsquo;ve also added a small info icon next to <strong>Sessions</strong> and{' '}
          <strong>New Users</strong>. Hover over one and you get a plain-English definition of what
          that metric actually counts — so there&rsquo;s no guessing whether
          &ldquo;sessions&rdquo; means people or visits.
        </p>
      </section>
    ),
  },
  {
    version: '1.1',
    summary: 'Webform leads, CSV export, and a rebuilt Ad Match Amount(s) tab',
    body: (
      <>
        {/* 1. Webform leads */}
        <section className="whats-new-item">
          <h2>Your web form leads, alongside your calls</h2>
          <p>
            The <strong>Call Tracking</strong> tab is now called <strong>Calls &amp; Leads</strong>,
            and it has two sub-tabs: <strong>Call Tracking</strong> and{' '}
            <strong>Webform Leads</strong>. Every inquiry submitted through the contact and
            &ldquo;request a consultation&rdquo; forms on your Amada website is now mirrored into the
            dashboard each night and automatically matched to your territory.
          </p>
          <p>
            That means the phone calls and the web form inquiries for your location finally live in
            one place, so you can see the full picture of the leads your marketing is generating —
            no more logging into WordPress to check who filled out a form.
          </p>
        </section>

        {/* 2. CSV export */}
        <section className="whats-new-item">
          <h2>Download your leads as a spreadsheet</h2>
          <p>
            Both the <strong>Call Tracking</strong> and <strong>Webform Leads</strong> sections now
            have a <strong>⬇ Download CSV</strong> button. One click exports your leads to a
            spreadsheet you can open in Excel or Google Sheets.
          </p>
          <p>
            On the Call Tracking side, the export respects whatever filters you have applied, so you
            can download exactly the set of calls you&rsquo;re looking at. Use it to work your leads,
            build a follow-up list, or share results with your team.
          </p>
        </section>

        {/* 3. Ad Match tab */}
        <section className="whats-new-item">
          <h2>A rebuilt Ad Match Amount(s) tab</h2>
          <p>
            The <strong>Ad Match Amount(s)</strong> tab has been reorganized into clear, at-a-glance
            boxes — <strong>Paid Campaign Spend Match</strong>, <strong>Territory Breakdown</strong>,
            and two brand-new programs:
          </p>

          <div className="whats-new-sub">
            <h3>Qualified Lead Program</h3>
            <p>
              We now help cover the cost of your <em>A Place for Mom</em> leads. Amada matches{' '}
              <strong>50%</strong> of your lead cost — currently about <strong>$29</strong> of the{' '}
              <strong>$58</strong> per lead — up to a monthly cap that scales with each
              territory&rsquo;s annualized revenue. The tab shows exactly how many matched leads per
              month you&rsquo;re eligible for and the maximum monthly amount we&rsquo;ll cover, plus
              who to contact to enroll.
            </p>
          </div>

          <div className="whats-new-sub">
            <h3>Paid Campaign Budget Allocation</h3>
            <p>
              You can now tell us how you&rsquo;d like each territory&rsquo;s paid ad budget split
              between channels. Google Ads is the default; drag the slider to shift some of your
              budget toward <strong>Meta</strong> (Facebook &amp; Instagram), and the pie chart
              updates to match. When you save, our marketing team is automatically notified so they
              can update your campaigns — nothing changes in your ad accounts until they do.{' '}
              <strong>YouTube</strong> and <strong>TikTok</strong> are coming soon.
            </p>
          </div>
        </section>
      </>
    ),
  },
];

export default function WhatsNew() {
  // Show whichever entry matches the running build at the top; fall back to the
  // newest entry if APP_VERSION is ever bumped without notes being added for it.
  const currentIndex = Math.max(
    0,
    RELEASES.findIndex((r) => r.version === APP_VERSION),
  );
  const current = RELEASES[currentIndex];
  const archive = RELEASES.filter((_, i) => i !== currentIndex);

  return (
    <div className="whats-new">
      <div className="whats-new-inner">
        <Link to="/" className="whats-new-back">
          ← Back to dashboard
        </Link>

        <header className="whats-new-header">
          <span className="whats-new-badge">Version {current.version}</span>
          <h1>What&rsquo;s new</h1>
          <p className="whats-new-lede">
            Here&rsquo;s what we&rsquo;ve added to your marketing dashboard in this release.
          </p>
        </header>

        {current.body}

        {archive.length > 0 && (
          <section className="whats-new-archive">
            <h2 className="whats-new-archive-title">Earlier releases</h2>
            {archive.map((release) => (
              <details key={release.version} className="whats-new-archive-item">
                <summary className="whats-new-archive-summary">
                  <span className="whats-new-archive-version">Version {release.version}</span>
                  <span className="whats-new-archive-teaser">{release.summary}</span>
                </summary>
                <div className="whats-new-archive-body">{release.body}</div>
              </details>
            ))}
          </section>
        )}

        <footer className="whats-new-footer">
          <Link to="/" className="whats-new-back">
            ← Back to dashboard
          </Link>
        </footer>
      </div>
    </div>
  );
}
